
import { useState } from 'react';
import { usePostActions } from '../hooks/usePosts';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Send } from 'lucide-react';
import CommentItem from './CommentItem';
import { useTranslation } from 'react-i18next';

interface CommentSectionProps {
  comments: any[];
  postId: string;
  onCommentAdded?: () => void;
  disabled?: boolean;
}

const CommentSection = ({ comments, postId, onCommentAdded, disabled = false }: CommentSectionProps) => {
  const { t } = useTranslation();
  const [commentText, setCommentText] = useState('');
  const { addComment, isCommenting } = usePostActions();
  const { toast } = useToast();

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!commentText.trim() || !postId) return;
    
    try {
      await addComment({ postId, content: commentText });
      setCommentText('');
      toast({
        title: t('comments.commentAdded'),
        description: t('comments.addedSuccess')
      });
      
      if (onCommentAdded) {
        onCommentAdded();
      }
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error.message || t('comments.addError'),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="mt-3 space-y-4">
      {comments.length > 0 ? (
        <div className="space-y-4">
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              postId={postId}
              onReplyAdded={onCommentAdded}
            />
          ))}
        </div>
      ) : (
        <p className="text-center text-sm text-muted-foreground">{t('comments.noComments')}</p>
      )}
      
      {!disabled && (
        <div className="pt-2 mt-3 border-t">
          <form className="flex items-center" onSubmit={handleCommentSubmit}>
            <input 
              type="text" 
              placeholder={t('comments.addComment')} 
              className="flex-1 bg-transparent text-sm outline-none" 
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              disabled={isCommenting}
            />
            <Button 
              type="submit" 
              variant="ghost"
              className="text-pet-purple font-semibold text-sm ml-2 p-0"
              disabled={!commentText.trim() || isCommenting}
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      )}
    </div>
  );
};

export default CommentSection;
